import { ApiaUtil, SimpleButton, useModal, LabelBox } from '@apia/components';
import { TApiaApiField } from '../ApiaApiHandler';
import { ApiaEnvironmentsModal } from '../modals/apia/components/ApiaEnvironmentsModal';
import { FiltersModal } from '../modals/apia/components/util/FiltersModal';
import { useId, useCallback, useState, useEffect } from 'react';
import { EventEmitter, TApiaFilter, getLabel } from '@apia/util';
import { responsiveTableStore } from '@apia/table';
import { Box, Label, spacing } from '@apia/theme';
import { useFormContext, validationsStore } from '@apia/validations';

export const ApiaEnvironmentsModalEmitter = new EventEmitter<{
  deleteFilters: TApiaFilter[];
}>();

export const ApiaApiEnvironmentsModalField = (props: TApiaApiField) => {
  const { name } = useFormContext();
  const [selectedRows, setSelectedRows] = useState<
    { id: string; title: string }[]
  >(
    props.element.value
      .split('#')
      .filter((env) => env !== '')
      .map((envData) => {
        const id = envData.split('_$_')[0];
        const title = envData.split('_$_')[1];

        return {
          id,
          title,
        };
      }),
  );
  const {
    isLoading: isFiltersModalLoading,
    show: showFiltersModal,
    ...filtersModalProps
  } = useModal();

  const id = useId();

  const deleteFilters = useCallback(() => {
    const newFilters: TApiaFilter[] = environmentsModal.filters?.map(
      (currentFilter) => {
        return {
          ...currentFilter,
          currentValue: '',
          deleteFiltersTimestamp: Date.now(),
        };
      },
    );
    ApiaEnvironmentsModalEmitter.emit('deleteFilters', newFilters);
  }, []);

  useEffect(() => {
    validationsStore.setFieldValue(
      name,
      props.element.name,
      selectedRows.map((e) => e.id).join('#'),
    );
  }, [name, props.element.name, selectedRows]);

  return (
    <>
      <Label>{getLabel(props.element.text).text}</Label>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(3),
        }}
      >
        <Box
          sx={{
            maxHeight: '200px',
            overflow: 'auto',
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
          }}
        >
          {selectedRows.map((row) => {
            return (
              <LabelBox
                key={row.id}
                label={row.title}
                onClose={() => {
                  setSelectedRows((prev) => {
                    return prev.filter((prevRow) => {
                      return prevRow.id !== row.id;
                    });
                  });
                }}
              />
            );
          })}
        </Box>
        <SimpleButton
          onClick={() => {
            ApiaUtil.instance.modals.open({
              onConfirm: () => {
                const rows =
                  responsiveTableStore.getState().responsiveTableSlice[id].rows;
                const selectedIndex =
                  responsiveTableStore.getState().responsiveTableSlice[id]
                    .selectedRows;

                const selectedRows: { id: string; title: string }[] = [];

                selectedIndex.forEach((i) => {
                  if (rows[i]) {
                    selectedRows.push({
                      id: rows[i].id,
                      title: rows[i].cells[1].children as string,
                    });
                  }
                });
                setSelectedRows((prev) => [
                  ...prev,
                  ...selectedRows.filter((newEnv) => {
                    return !prev.find((prevEnv) => prevEnv.id === newEnv.id);
                  }),
                ]);
              },
              confirmProps: { confirmButtonText: getLabel('btnCon').text },
              children: (
                <ApiaEnvironmentsModal
                  {...filtersModalProps}
                  initialProps={props.element}
                  tableName={id}
                />
              ),
              size: 'xxl',
              title: environmentsModal.label,
              NavBar: (
                <>
                  <SimpleButton
                    variant={'outline-sm'}
                    title={window.FILTERS_LABEL}
                    onClick={() => {
                      ApiaUtil.instance.modals.open({
                        children: <FiltersModal tableName={id} />,
                        variant:
                          'layout.common.modals.tableModal.additionalFiltersModal',
                        size: 'xl',
                        noHeader: true,
                        initialFocusGetter(modalInnerRef) {
                          return modalInnerRef.querySelector(
                            'select',
                          ) as HTMLElement;
                        },
                      });
                    }}
                    isLoading={isFiltersModalLoading}
                  >
                    {window.FILTERS_LABEL}
                  </SimpleButton>
                  <SimpleButton
                    variant={'outline-sm'}
                    onClick={deleteFilters}
                    title={window.BTN_DELETE_FILTERS}
                  >
                    {window.BTN_DELETE_FILTERS}
                  </SimpleButton>
                </>
              ),
            });
          }}
          sx={{
            width: 'fit-content !important',
          }}
        >
          {getLabel('lblAdd').text}
        </SimpleButton>
      </Box>
    </>
  );
};
